import { create } from 'zustand';
import type { WatchlistItem, WatchProgress, ThemeColor, Mood } from '../types';
import {
  getWatchlist,
  addToWatchlist as addToWatchlistStorage,
  removeFromWatchlist as removeFromWatchlistStorage,
  getContinueWatching,
  getTheme,
  saveTheme,
  addToSmartWatchlist,
} from '../services/storage';

interface AppState {
  // Theme
  themeColor: ThemeColor;
  setThemeColor: (color: ThemeColor) => void;

  // Watchlist
  watchlist: WatchlistItem[];
  loadWatchlist: () => void;
  addToWatchlist: (item: WatchlistItem) => void;
  removeFromWatchlist: (mediaType: 'movie' | 'tv', id: number) => void;
  isInWatchlist: (mediaType: 'movie' | 'tv', id: number) => boolean;

  // Continue Watching
  continueWatching: WatchProgress[];
  loadContinueWatching: () => void;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isSearchOpen: boolean;
  setSearchOpen: (open: boolean) => void;

  // Mood (Fun feature!)
  currentMood: Mood | null;
  setMood: (mood: Mood | null) => void;

  // UI State
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;

  // Modal
  trailerKey: string | null;
  setTrailerKey: (key: string | null) => void;

  // Loading states
  isLoading: boolean;
  setLoading: (loading: boolean) => void;

  // Easter eggs
  konami: boolean;
  setKonami: (enabled: boolean) => void;
  secretsFound: number;
  incrementSecrets: () => void;
}

export const useStore = create<AppState>((set, get) => ({
  // Theme
  themeColor: getTheme(),
  setThemeColor: (color) => {
    saveTheme(color);
    set({ themeColor: color });
    // Update CSS custom property
    document.documentElement.style.setProperty('--color-primary', getThemeHex(color));
  },

  // Watchlist
  watchlist: [],
  loadWatchlist: () => {
    set({ watchlist: getWatchlist() });
  },
  addToWatchlist: (item) => {
    addToWatchlistStorage(item);
    // Also add to smart watchlist
    addToSmartWatchlist({
      id: item.id,
      tmdbId: item.id,
      title: item.title,
      type: item.mediaType === 'movie' ? 'movie' : 'tv',
      posterPath: item.posterPath || '',
      backdropPath: item.backdropPath || undefined,
      status: 'want_to_watch',
      priority: 'normal',
      addedAt: item.addedAt || Date.now(),
      tracking: { enabled: false, notifyMe: false },
    });
    set({ watchlist: getWatchlist() });
  },
  removeFromWatchlist: (mediaType, id) => {
    removeFromWatchlistStorage(mediaType, id);
    set({ watchlist: getWatchlist() });
  },
  isInWatchlist: (mediaType, id) => {
    return get().watchlist.some(i => i.id === id && i.mediaType === mediaType);
  },

  // Continue Watching
  continueWatching: [],
  loadContinueWatching: () => {
    set({ continueWatching: getContinueWatching() });
  },

  // Search
  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),
  isSearchOpen: false,
  setSearchOpen: (open) => set({ isSearchOpen: open }),

  // Mood
  currentMood: null,
  setMood: (mood) => set({ currentMood: mood }),

  // UI State
  isSidebarOpen: false,
  setSidebarOpen: (open) => set({ isSidebarOpen: open }),

  // Modal
  trailerKey: null,
  setTrailerKey: (key) => set({ trailerKey: key }),

  // Loading
  isLoading: false,
  setLoading: (loading) => set({ isLoading: loading }),

  // Easter eggs
  konami: false,
  setKonami: (enabled) => set({ konami: enabled }),
  secretsFound: 0,
  incrementSecrets: () => set((state) => ({ secretsFound: state.secretsFound + 1 })),
}));

// Helper to get theme hex
function getThemeHex(color: ThemeColor): string {
  const colors: Record<ThemeColor, string> = {
    red: '#E50914',
    blue: '#5865F2',
    purple: '#9146FF',
    green: '#1DB954',
    orange: '#FF7B00',
    pink: '#E91E63',
  };
  return colors[color];
}

// Initialize theme on load
if (typeof window !== 'undefined') {
  const theme = getTheme();
  document.documentElement.style.setProperty('--color-primary', getThemeHex(theme));
}
